import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import "../styles/components/SiteHeader.css";
import logo from "../assets/logo.png";

const navItems = [
  { to: "/", label: "HOME", end: true },
  { to: "/agenda", label: "AGENDA", hidden: true },
  { to: "/awards", label: "AWARDS", hidden: true },
  { to: "/travel-hotel", label: "TRAVEL & HOTEL" },
  { to: "/contact", label: "CONTACT US" },
];

export default function SiteHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuIdSeed = useId();
  const mobileMenuId = `site-header-mobile-menu-${menuIdSeed}`;

  const menuPanelRef = useRef(null);
  const hamburgerButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const shouldReturnFocusRef = useRef(false);

  const visibleNavItems = navItems.filter((item) => !item.hidden);

  const closeMobileMenu = () => {
    shouldReturnFocusRef.current = true;
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!mobileMenuOpen) {
      if (shouldReturnFocusRef.current) {
        shouldReturnFocusRef.current = false;
        hamburgerButtonRef.current?.focus();
      }
      return;
    }

    const getFocusableElements = () => {
      const focusables = [];

      const menuPanel = menuPanelRef.current;
      if (!menuPanel) return focusables;

      const selectors = [
        'a[href]:not([tabindex="-1"])',
        'button:not([disabled]):not([tabindex="-1"])',
        'input:not([disabled]):not([tabindex="-1"])',
        'select:not([disabled]):not([tabindex="-1"])',
        'textarea:not([disabled]):not([tabindex="-1"])',
        '[tabindex]:not([tabindex="-1"])',
      ];
      const menuFocusables = Array.from(menuPanel.querySelectorAll(selectors.join(","))).filter(
        (el) => el.getClientRects().length > 0,
      );

      return focusables.concat(menuFocusables);
    };

    const focusMenuStart = () => {
      closeButtonRef.current?.focus();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMobileMenu();
        return;
      }

      if (e.key !== "Tab") return;

      const focusables = getFocusableElements();
      if (focusables.length === 0) return;

      const active = document.activeElement;
      const currentIndex = focusables.indexOf(active);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (active === first || currentIndex === -1) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onPointerDown = (e) => {
      const menuPanel = menuPanelRef.current;
      const hamburgerButton = hamburgerButtonRef.current;
      const target = e.target;

      if (!menuPanel || !hamburgerButton) return;
      if (menuPanel.contains(target) || hamburgerButton.contains(target)) return;

      closeMobileMenu();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);

    // Ensure focus moves into the menu after it becomes visible.
    const focusTimer = window.setTimeout(focusMenuStart, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");

    const onChange = () => {
      if (!media.matches) {
        shouldReturnFocusRef.current = false;
        setMobileMenuOpen(false);
      }
    };

    onChange();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const mobileMenu = (
    <div
      className={`site-header__mobileOverlay${mobileMenuOpen ? " site-header__mobileOverlay--open" : ""}`}
      aria-hidden={!mobileMenuOpen}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeMobileMenu();
      }}
    >
      <div
        id={mobileMenuId}
        ref={menuPanelRef}
        className="site-header__mobilePanel"
        role="dialog"
        aria-modal={mobileMenuOpen ? "true" : undefined}
        aria-label="Site menu"
        tabIndex={-1}
      >
        <div className="site-header__mobilePanelHeader">
          <NavLink className="site-header__mobileLogo" to="/" aria-label="Moët Hennessy USA">
            <img src={logo} alt="Moët Hennessy USA" />
          </NavLink>
          <button
            ref={closeButtonRef}
            type="button"
            className="site-header__mobileClose"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          >
            <span className="site-header__closeIcon" aria-hidden="true" />
          </button>
        </div>
        <nav className="site-header__mobileNav" aria-label="Primary">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => {
                trackEvent("navigation_click", {
                  link_text: item.label,
                  link_url: item.to,
                  nav_type: "mobile",
                });
                closeMobileMenu();
              }}
              className={({ isActive }) =>
                `site-header__navLink${isActive ? " site-header__navLink--active" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );

  return (
    <>
      <header className="site-header">
        <div className="container site-header__inner">
          <NavLink className="site-header__logo" to="/" aria-label="Moët Hennessy USA">
            <img src={logo} alt="Moët Hennessy USA" />
          </NavLink>
          <nav className="site-header__nav" aria-label="Primary">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() =>
                  trackEvent("navigation_click", {
                    link_text: item.label,
                    link_url: item.to,
                    nav_type: "desktop",
                  })
                }
                className={({ isActive }) =>
                  `site-header__navLink${isActive ? " site-header__navLink--active" : ""}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button
            ref={hamburgerButtonRef}
            type="button"
            className={`site-header__hamburger${mobileMenuOpen ? " site-header__hamburger--open" : ""}`}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuId}
            onClick={() => {
              trackEvent("mobile_menu_toggle", {
                action: mobileMenuOpen ? "close" : "open",
              });
              setMobileMenuOpen((open) => !open);
            }}
          >
            <span className="site-header__hamburgerIcon" aria-hidden="true">
              <span className="site-header__hamburgerLine" />
              <span className="site-header__hamburgerLine" />
              <span className="site-header__hamburgerLine" />
            </span>
          </button>
        </div>
      </header>
      {typeof document !== "undefined" ? createPortal(mobileMenu, document.body) : null}
    </>
  );
}
